import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

import api from '../../services/api';

function ReceiptUpload({ receiptStatus, receiptFilename, hintText, onUpload, transactionId }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Sync internal state when parent changes receiptStatus (e.g., after reset)
  React.useEffect(() => {
    if (receiptStatus === 'none') {
      setSelectedFile(null);
    }
  }, [receiptStatus]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (onUpload) {
        onUpload(file);
      }
      event.target.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handlePreview = async () => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    } else if (transactionId) {
      // Fetch presigned S3 URL from backend
      try {
        const res = await api.get(`/compliance/transaction/${transactionId}/receipt-url`);
        setPreviewUrl(res.data?.data?.url || null);
      } catch (e) {
        console.error('Failed to get receipt URL', e);
        setPreviewUrl(null);
      }
    } else {
      setPreviewUrl(null);
    }
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl && selectedFile) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const isPdf = (name) => name?.toLowerCase().endsWith('.pdf');

  const fileInput = <input type="file" ref={fileInputRef} onChange={handleFileSelect}
    aria-label="Receipt file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: 'none' }} />;
  if (selectedFile || receiptStatus === 'uploaded' || receiptStatus === 'selected') {
    const pending = receiptStatus === 'selected';
    const displayName = selectedFile ? selectedFile.name : receiptFilename || 'receipt.pdf';
    return (
      <>
        {fileInput}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={pending ? <UploadFileIcon /> : <CheckCircleIcon />}
            label={displayName}
            color={pending ? 'info' : 'success'}
            variant="outlined"
            size="small"
          />
          <IconButton size="small" onClick={handlePreview} title="View receipt">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          {pending && <Button size="small" onClick={handleButtonClick}>Replace receipt</Button>}
        </Box>
        {pending && <Typography variant="caption" color="text.secondary">Selected — saved when you confirm the charge.</Typography>}
        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Typography variant="subtitle1">{displayName}</Typography>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '75vh' }}>
            {previewUrl ? (
              isPdf(displayName) ? (
                <iframe src={previewUrl} width="100%" height="100%" style={{ border: 'none' }} title="Receipt preview" />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
                  <img src={previewUrl} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </Box>
              )
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">Could not load the receipt preview. Please try again.</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (receiptStatus === 'not_required') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        Receipt not required (statement is self-evident)
      </Typography>
    );
  }

  return (
    <Box>
      {fileInput}
      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadFileIcon />}
        onClick={handleButtonClick}
        sx={{ mb: hintText ? 0.5 : 0 }}
      >
        Upload Receipt
      </Button>
      {hintText && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {hintText}
        </Typography>
      )}
    </Box>
  );
}

export default ReceiptUpload;
